import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Bio } from "../../data/constants";
import { BtnPrimary, BtnGhost } from "../../studio/ui";
import { Mandala } from "../../studio/SacredGeometry";
import { FiDownload, FiMail } from "react-icons/fi";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import heroImg from "../../images/HeroImage.jpg";

/* ============================================================
   HERO — DIVINE FUTURISM
   Obsidian cosmic void · sacred geometry · a central glowing orb
   crowned in halo light. Beeple-meets-Michelangelo, still legible.
   The orb is PURE CSS/SVG — no Three.js, no 3D libraries.
============================================================ */

/* local entrance — content rises (the "ascend" metaphor) */
const rise = keyframes`
  from { opacity: 0; transform: translateY(26px); filter: blur(6px); }
  to   { opacity: 1; transform: translateY(0); filter: blur(0); }
`;

const Wrap = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: 40px;
  max-width: 1240px;
  margin: 0 auto;
  overflow: visible;
  padding: 132px 40px 96px;
  background: transparent;

  @media ${({ theme }) => theme.bp.lg} {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    gap: 20px;
    padding: 120px 24px 90px;
  }

  /* extra local celestial glows over the void */
  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(120px);
    z-index: 0;
  }
  &::before {
    width: 760px;
    height: 760px;
    top: -16%;
    left: 50%;
    transform: translateX(-50%);
    background: radial-gradient(
      circle,
      ${({ theme }) => theme.violetGlow},
      transparent 66%
    );
  }
  &::after {
    width: 620px;
    height: 620px;
    bottom: -22%;
    left: 50%;
    transform: translateX(-50%);
    background: radial-gradient(
      circle,
      ${({ theme }) => theme.goldGlow},
      transparent 68%
    );
  }

  @media ${({ theme }) => theme.bp.md} {
    padding: 116px 18px 80px;
  }
`;

/* ---------- the orb + its sacred-geometry backdrop ---------- */

const Stage = styled.div`
  position: relative;
  z-index: 1;
  order: 2; /* right column on desktop */
  justify-self: center;
  width: clamp(280px, 34vw, 420px);
  height: clamp(280px, 34vw, 420px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${rise} 1s ease both;

  @media ${({ theme }) => theme.bp.lg} {
    order: 1; /* orb on top when stacked */
    margin-bottom: 8px;
  }
  @media ${({ theme }) => theme.bp.md} {
    width: clamp(240px, 72vw, 340px);
    height: clamp(240px, 72vw, 340px);
  }
`;

/* rotating mandala behind the orb */
const Sacred = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  animation: spin-slow 70s linear infinite;

  svg {
    width: 114%;
    height: 114%;
  }
`;

/* a slim counter-rotating dashed ring */
const CounterRing = styled.div`
  position: absolute;
  inset: 8%;
  border-radius: 50%;
  border: 1px dashed ${({ theme }) => theme.border};
  animation: spin-rev 48s linear infinite;
`;

/* breathing float wrapper (separates transform animations from pulse) */
const Float = styled.div`
  position: relative;
  width: 64%;
  height: 64%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: float-up 7s ease-in-out infinite;
`;

/* the luminous sphere itself */
const Orb = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  /* sphere shading: bright offset highlight → indigo core → dark rim */
  background:
    radial-gradient(
      circle at 36% 30%,
      rgba(245, 241, 230, 0.95) 0%,
      ${({ theme }) => theme.goldSoft} 9%,
      ${({ theme }) => theme.violet} 38%,
      ${({ theme }) => theme.indigo} 62%,
      ${({ theme }) => theme.bgDeep} 100%
    );
  box-shadow:
    /* inner sheen */
    inset 0 0 60px -8px rgba(245, 241, 230, 0.35),
    inset -28px -34px 70px -10px rgba(4, 4, 8, 0.9),
    /* concentric halo of light */
    0 0 0 1px ${({ theme }) => theme.borderHi},
    0 0 36px 2px ${({ theme }) => theme.haloGlow},
    0 0 90px 8px ${({ theme }) => theme.violetGlow},
    0 0 150px 24px ${({ theme }) => theme.goldGlow};
`;

/* a glowing ring that circles around the photo (photo stays upright) */
const PhotoRing = styled.div`
  position: absolute;
  inset: 0%;
  border-radius: 50%;
  z-index: 2;
  pointer-events: none;
  border: 1px dashed ${({ theme }) => theme.borderHi};
  /* gold conic sheen rotating around the rim */
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    ${({ theme }) => theme.goldGlow} 40deg,
    transparent 110deg,
    transparent 250deg,
    ${({ theme }) => theme.violetGlow} 300deg,
    transparent 340deg
  );
  -webkit-mask: radial-gradient(circle, transparent 64%, #000 66%);
  mask: radial-gradient(circle, transparent 64%, #000 66%);
  animation: spin-slow 16s linear infinite;
`;

/* portrait fills the orb — covers the violet band entirely,
   leaving only the outer gold rim + halo glow around it */
const Portrait = styled.div`
  position: absolute;
  inset: 4%;
  border-radius: 50%;
  overflow: hidden;
  z-index: 2;
  border: 1px solid ${({ theme }) => theme.borderHi};
  box-shadow: 0 0 0 6px rgba(11, 10, 24, 0.55),
    0 0 30px 4px ${({ theme }) => theme.goldGlow},
    inset 0 0 26px rgba(11, 10, 24, 0.6);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* tint the photo to sit in the celestial palette */
    filter: saturate(0.9) contrast(1.02) brightness(1.02);
  }

  /* gold/violet vignette so the portrait melts into the orb */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle at 50% 42%,
      transparent 70%,
      rgba(11, 10, 24, 0.45) 100%
    );
    pointer-events: none;
  }
`;

/* concentric halo rings radiating outward like a crown / neural net */
const Halo = styled.div`
  position: absolute;
  inset: ${({ $inset }) => $inset};
  border-radius: 50%;
  border: 1px solid ${({ $violet, theme }) =>
    $violet ? theme.borderViolet : theme.border};
  box-shadow: 0 0 40px -6px
    ${({ $violet, theme }) => ($violet ? theme.violetGlow : theme.goldGlow)};
  animation: halo-pulse ${({ $dur }) => $dur} ease-in-out infinite;
  pointer-events: none;
`;

/* ---------- typographic content ---------- */

const Content = styled.div`
  position: relative;
  z-index: 2;
  order: 1; /* left column on desktop */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  max-width: 620px;
  animation: ${rise} 1s ease 0.15s both;

  @media ${({ theme }) => theme.bp.lg} {
    order: 2;
    align-items: center;
    text-align: center;
  }
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11.5px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.goldSoft};
  background: ${({ theme }) => theme.glass};
  border: 1px solid ${({ theme }) => theme.borderHi};
  border-radius: 999px;
  padding: 7px 16px;
  backdrop-filter: blur(8px);
  margin-bottom: 26px;

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.gold};
    box-shadow: 0 0 10px 2px ${({ theme }) => theme.goldGlow};
    animation: halo-pulse 2.4s ease-in-out infinite;
  }
`;

const Name = styled.h1`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: 600;
  font-size: clamp(46px, 9vw, 110px);
  line-height: 1.02;
  letter-spacing: 0.01em;
  margin: 0;
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.gold} 0%,
    ${({ theme }) => theme.white} 42%,
    ${({ theme }) => theme.goldSoft} 58%,
    ${({ theme }) => theme.violet} 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 0 28px ${({ theme }) => theme.haloGlow});
  animation: shimmer 6s linear infinite;
`;

const Role = styled.div`
  margin-top: 18px;
  min-height: 1.5em;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: clamp(14px, 2.2vw, 19px);
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.gold};

  .rotor {
    display: inline-block;
    animation: ${rise} 0.5s ease both;
  }
  .at {
    display: block;
    margin-top: 8px;
    font-family: ${({ theme }) => theme.fonts.serif};
    font-style: italic;
    font-size: clamp(15px, 2vw, 20px);
    letter-spacing: 0.02em;
    color: ${({ theme }) => theme.textMuted};
  }
`;

const Headline = styled.p`
  margin: 24px 0 0;
  max-width: 540px;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 16.5px;
  line-height: 1.7;
  color: ${({ theme }) => theme.textMuted};

  @media ${({ theme }) => theme.bp.lg} {
    margin: 24px auto 0;
  }
`;

const Actions = styled.div`
  margin-top: 34px;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: flex-start;

  @media ${({ theme }) => theme.bp.lg} {
    justify-content: center;
  }
`;

const Socials = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-start;

  @media ${({ theme }) => theme.bp.lg} {
    justify-content: center;
  }

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 12px;
    font-size: 19px;
    color: ${({ theme }) => theme.textMuted};
    background: ${({ theme }) => theme.glass};
    border: 1px solid ${({ theme }) => theme.border};
    backdrop-filter: blur(8px);
    transition: all 0.28s ease;

    &:hover {
      color: ${({ theme }) => theme.gold};
      border-color: ${({ theme }) => theme.gold};
      transform: translateY(-3px);
      box-shadow: 0 0 24px -4px ${({ theme }) => theme.goldGlow};
    }
  }
`;

/* ---------- ascend scroll hint ---------- */

const Scroll = styled.div`
  position: absolute;
  bottom: 26px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  animation: float-up 3.4s ease-in-out infinite;

  .chev {
    width: 13px;
    height: 13px;
    border-top: 1.5px solid ${({ theme }) => theme.gold};
    border-right: 1.5px solid ${({ theme }) => theme.gold};
    transform: rotate(-45deg);
    opacity: 0.85;
  }
  .line {
    width: 1px;
    height: 26px;
    background: linear-gradient(
      to top,
      transparent,
      ${({ theme }) => theme.gold}
    );
  }
  .label {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 10px;
    letter-spacing: 0.34em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textFaint};
  }

  @media ${({ theme }) => theme.bp.md} {
    display: none;
  }
`;

export default function HeroSection() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setIdx((i) => (i + 1) % Bio.roles.length),
      2600
    );
    return () => clearInterval(id);
  }, []);

  return (
    <Wrap id="home">
      <Stage>
        <Sacred>
          <Mandala size={620} />
        </Sacred>
        <CounterRing />
        <Halo $inset="3%" $dur="6.5s" />
        <Halo $inset="-3%" $dur="8s" $violet />
        <Halo $inset="-9%" $dur="9.5s" />
        <Float>
          <Orb>
            <PhotoRing />
            <Portrait>
              <img src={heroImg} alt={Bio.name} />
            </Portrait>
          </Orb>
        </Float>
      </Stage>

      <Content>
        {Bio.available && (
          <StatusPill>
            <span className="dot" />
            Available
          </StatusPill>
        )}

        <Name>{Bio.name}</Name>

        <Role>
          <span className="rotor" key={idx}>
            {Bio.roles[idx]}
          </span>
          <span className="at">
            {Bio.title} @ {Bio.company}
          </span>
        </Role>

        <Headline>{Bio.headline}</Headline>

        <Actions>
          <BtnPrimary href={Bio.resume} target="_blank" rel="noreferrer">
            <FiDownload /> Download Résumé
          </BtnPrimary>
          <BtnGhost href="#contact">
            <FiMail /> Contact Me
          </BtnGhost>
        </Actions>

        <Socials>
          <a
            href={Bio.github}
            target="_blank"
            rel="noreferrer"
            aria-label="GitHub"
          >
            <FaGithub />
          </a>
          <a
            href={Bio.linkedin}
            target="_blank"
            rel="noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedin />
          </a>
        </Socials>
      </Content>

      <Scroll>
        <span className="chev" />
        <span className="line" />
        <span className="label">Ascend</span>
      </Scroll>
    </Wrap>
  );
}
